import {
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useState } from "react";
import { openInNewTab } from "../../../../utils/openInNewTab";
import { Benefit, BenefitFaq } from "../../data";
import styles from "./index.module.scss";

const FaqAnswer: React.FC<{ answer: BenefitFaq["answer"] }> = props =>
  Array.isArray(props.answer) ? (
    <>
      {props.answer.map((paragraph, index) => (
        <WSText.ParagraphSm color="gray600" key={index}>
          {paragraph}
        </WSText.ParagraphSm>
      ))}
    </>
  ) : (
    <WSText.ParagraphSm color="gray600" as="div">
      {props.answer}
    </WSText.ParagraphSm>
  );

/** The main column of the detail page: everything the catalog says about a benefit. */
export const BenefitDetailSections: React.FC<{ benefit: Benefit }> = props => {
  const [isFaqsExpanded, setIsFaqsExpanded] = useState(false);
  const { benefit } = props;

  return (
    <WSCard>
      <WSList gap="XL">
        {benefit.mainSubheader && (
          <WSText.Heading5>{benefit.mainSubheader}</WSText.Heading5>
        )}

        {benefit.videoUrl && (
          <WSElement className={styles.videoContainer}>
            <iframe
              className={styles.video}
              frameBorder="0"
              allowFullScreen
              title={benefit.mainHeader}
              src={benefit.videoUrl}
            />
          </WSElement>
        )}

        {benefit.mainDescription && (
          <WSElement>
            {typeof benefit.mainDescription === "string" ? (
              <WSText>{benefit.mainDescription}</WSText>
            ) : (
              benefit.mainDescription
            )}
          </WSElement>
        )}

        {benefit.mainDescriptionList && (
          <WSElement as="ul" ml="XL">
            {benefit.mainDescriptionList.map((item, index) => (
              <WSElement key={index} as="li">
                <WSText mt={index === 0 ? "NONE" : "XS"}>{item}</WSText>
              </WSElement>
            ))}
          </WSElement>
        )}

        {benefit.mainCards && (
          <WSGrid>
            {benefit.mainCards.map((card, index) => (
              <WSGrid.Item span={{ xs: "6", l: "4" }} key={index}>
                <WSCard
                  className={styles.contentCard}
                  header={{
                    label: {
                      avatar: {
                        type: "icon",
                        colorBackground: "blue50",
                        color: "blue500",
                        icon: card.icon || "check-envelope"
                      }
                    }
                  }}
                >
                  <WSList gap="S">
                    {card.lines.map(line => (
                      <WSText.ParagraphSm color="gray600" key={line}>
                        {line}
                      </WSText.ParagraphSm>
                    ))}
                  </WSList>
                </WSCard>
              </WSGrid.Item>
            ))}
          </WSGrid>
        )}

        {benefit.mainInfoListSteps && (
          <WSElement>
            <WSText.Heading4 mb="XL">
              {benefit.mainInfoListHeader || "How it works"}
            </WSText.Heading4>
            {benefit.mainInfoListSteps.map((step, index) => (
              <WSFlexBox
                key={index}
                mt={index === 0 ? "NONE" : "XL"}
                wrap="nowrap"
              >
                <WSElement>
                  <WSText weight="medium" mr="M">
                    {index + 1}.
                  </WSText>
                </WSElement>
                <WSElement>
                  <WSText weight="medium">{step.title}</WSText>
                  {step.description?.map((line, lineIndex) => (
                    <WSText key={lineIndex}>{line}</WSText>
                  ))}
                </WSElement>
              </WSFlexBox>
            ))}
          </WSElement>
        )}

        {benefit.mainFaqs && benefit.mainFaqs.length > 0 && (
          <WSElement>
            <WSFlexBox
              gap="L"
              alignItems="center"
              onClick={() => setIsFaqsExpanded(!isFaqsExpanded)}
            >
              <WSText.Heading4>
                {benefit.mainFaqsHeader || "FAQs"}
              </WSText.Heading4>
              <WSIcon name={isFaqsExpanded ? "caret-up" : "caret-down"} />
            </WSFlexBox>

            {isFaqsExpanded && (
              <WSElement mt="L">
                {benefit.mainFaqs.map((faq, index) => (
                  <WSElement key={index} mt={index === 0 ? "NONE" : "XL"}>
                    <WSText weight="medium" mb="XS">
                      {faq.question}
                    </WSText>
                    <FaqAnswer answer={faq.answer} />
                  </WSElement>
                ))}
              </WSElement>
            )}
          </WSElement>
        )}

        {benefit.mainLearnMoreLink && (
          <WSElement>
            <WSButton.Link
              onClick={() => openInNewTab(benefit.mainLearnMoreLink!)}
              rightIcon="open-tab"
            >
              {benefit.mainLearnMoreLinkText ||
                `Learn more about ${benefit.name}...`}
            </WSButton.Link>
          </WSElement>
        )}

        {benefit.mainFootnote && (
          <WSText color="gray500">{benefit.mainFootnote}</WSText>
        )}
      </WSList>
    </WSCard>
  );
};
